import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import CenteredVideoPlayer from './CenteredVideoPlayer';
import { config } from '../config';
import sessionTracking from '../utils/sessionTracking';
import { apiService } from '../services/apiService';

/**
 * Shared component for rendering blog elements
 * Used by both MobileBlogPreview and PublicBlogPage to ensure identical rendering
 */

// Consistent spacing between elements (in rem units)
const ELEMENT_SPACING = 0.5; // 0.5rem = 8px spacing between elements

// Padding constants for blog elements (in pixels)
const BLOG_ELEMENT_PADDING = {
  top: 10,      // Top padding for all elements
  bottom: 0,    // Bottom padding for all elements
  left: 10,     // Left padding for text/title/button elements
  right: 10,    // Right padding for text/title/button elements
  mediaLeft: 0, // Left padding for image/video/offer elements
  mediaRight: 0 // Right padding for image/video/offer elements
};

const BlogElementRenderer = ({ element, index, isMobile = true, affiliateId, offerId, pageNumber, pageId, blogPostId, sourceBlogId, offerElementId, sessionId }) => {
  // Get padding values from element styles or use defaults
  const elementPadding = {
    top: element.styles?.paddingTop || element.styles?.top || element.styles?.py || ELEMENT_SPACING,
    bottom: element.styles?.paddingBottom || element.styles?.bottom || element.styles?.py || ELEMENT_SPACING,
    left: element.styles?.paddingLeft || element.styles?.left || element.styles?.px || 2,
    right: element.styles?.paddingRight || element.styles?.right || element.styles?.px || 2
  };

  // Get size from element styles or use default
  const elementSize = element.styles?.size || 100;

  // Helper to fix localhost URLs when accessing from other devices
  const processUrl = (url) => {
    if (!url) return '#';
    if (typeof window !== 'undefined' && url.includes('localhost')) {
      const currentHostname = window.location.hostname;
      if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
        return url.replace('localhost', currentHostname);
      }
    }
    return url;
  };

  switch (element.type) {
    case 'title':
      return element.content ? (
        (() => {
          // Check if content contains HTML tags
          const hasHtmlTags = /<[^>]+>/.test(element.content);

          const commonStyles = {
            margin: 0,
            fontSize: isMobile ? '1.2rem' : '1.4rem',
            textAlign: 'center',
            transform: `scale(${elementSize / 100})`,
            transformOrigin: 'top center',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            // Use element.styles.pt if explicitly set to 0, otherwise use default padding
            pt: (element.styles?.pt === 0 || element.styles?.paddingTop === 0) ? 0 : `${BLOG_ELEMENT_PADDING.top}px`,
            pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
            pl: `${BLOG_ELEMENT_PADDING.left}px`,
            pr: `${BLOG_ELEMENT_PADDING.right}px`,
            mt: 0, // Ensure no top margin
            '& *': {
              maxWidth: '100%',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            },
            ...element.styles
          };

          if (hasHtmlTags) {
            return (
              <Box
                key={element.id || index}
                sx={commonStyles}
                dangerouslySetInnerHTML={{ __html: element.content }}
              />
            );
          }

          return (
            <Typography
              key={element.id || index}
              variant="h4"
              sx={{
                ...commonStyles,
                whiteSpace: 'pre-wrap'
              }}
            >
              {element.content}
            </Typography>
          );
        })()
      ) : null;

    case 'text':
      return element.content ? (
        (() => {
          // Check if content contains HTML tags
          const hasHtmlTags = /<[^>]+>/.test(element.content);

          const commonStyles = {
            margin: 0,
            lineHeight: 1.6,
            fontSize: isMobile ? '0.9rem' : '1rem',
            textAlign: element.styles?.textAlign || 'left',
            transform: `scale(${elementSize / 100})`,
            transformOrigin: 'top center',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            pt: `${BLOG_ELEMENT_PADDING.top}px`,
            pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
            pl: `${BLOG_ELEMENT_PADDING.left}px`,
            pr: `${BLOG_ELEMENT_PADDING.right}px`,
            '& *': {
              maxWidth: '100%',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            },
            ...element.styles
          };

          if (hasHtmlTags) {
            return (
              <Box
                key={element.id || index}
                sx={commonStyles}
                dangerouslySetInnerHTML={{ __html: element.content }}
              />
            );
          }

          return (
            <Typography
              key={element.id || index}
              variant="body1"
              sx={{
                ...commonStyles,
                whiteSpace: 'pre-wrap'
              }}
            >
              {element.content}
            </Typography>
          );
        })()
      ) : null;

    case 'bullets':
      return element.rows && element.rows.length > 0 ? (
        <Box
          key={element.id || index}
          sx={{
            ...element.styles,
            pt: `${BLOG_ELEMENT_PADDING.top}px`,
            pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
            pl: `${BLOG_ELEMENT_PADDING.left}px`,
            pr: `${BLOG_ELEMENT_PADDING.right}px`,
            transform: `scale(${elementSize / 100})`,
            transformOrigin: 'top center'
          }}
        >
          {element.rows.map((row, rowIndex) => {
            // Render icon based on type
            const renderIcon = () => {
              switch (row.icon) {
                case 'check':
                  return <Box sx={{ color: '#b6ff3c', fontSize: '1.2rem' }}>✓</Box>;
                case 'red-x':
                  return <Box sx={{ color: '#e53e3e', fontWeight: 'bold', fontSize: '1.2rem' }}>✗</Box>;
                case 'pink-check':
                  return (
                    <Box sx={{
                      width: '24px', height: '24px', borderRadius: '50%',
                      backgroundColor: '#ff9a9e', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', color: 'white', fontWeight: 'bold'
                    }}>✓</Box>
                  );
                case 'dark-check':
                  return (
                    <Box sx={{
                      width: '24px', height: '24px', borderRadius: '4px',
                      backgroundColor: '#2d3748', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', color: 'white', fontWeight: 'bold'
                    }}>✓</Box>
                  );
                case 'green-badge':
                  return (
                    <Box sx={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      backgroundColor: '#68d391', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.1rem', color: 'white', fontWeight: 'bold',
                      border: '2px solid #48bb78'
                    }}>✓</Box>
                  );
                case 'star-badge':
                  return (
                    <Box sx={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1.1rem', color: 'white', fontWeight: 'bold',
                      border: '2px solid #5a67d8'
                    }}>⭐</Box>
                  );
                case 'star':
                  return <Box sx={{ color: '#ffe066', fontSize: '1.2rem' }}>★</Box>;
                case 'info':
                  return <Box sx={{ color: '#40c4ff', fontSize: '1.2rem' }}>ℹ</Box>;
                case 'warning':
                  return <Box sx={{ color: '#ffb347', fontSize: '1.2rem' }}>⚠</Box>;
                case 'green-number':
                  return (
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '6px',
                      background: '#22c55e', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 'bold', fontSize: '1.1rem'
                    }}>{rowIndex + 1}</Box>
                  );
                case 'circle-number':
                  return (
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '50%',
                      border: '2px solid #22c55e', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      color: '#22c55e', fontWeight: 'bold', fontSize: '1.1rem',
                      background: 'white'
                    }}>{rowIndex + 1}</Box>
                  );
                default:
                  return <Box sx={{ fontSize: '1.2rem' }}>•</Box>;
              }
            };

            return (
              <Box
                key={rowIndex}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  mb: rowIndex < element.rows.length - 1 ? 2 : 0
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', minWidth: '32px' }}>
                  {renderIcon()}
                </Box>
                <Typography
                  component="span"
                  sx={{
                    color: element.styles?.color || '#101022',
                    fontSize: element.styles?.fontSize || '1.25rem',
                    fontFamily: element.styles?.fontFamily || 'Inter, Arial, sans-serif',
                    lineHeight: 1.5,
                    flex: 1,
                    textTransform: element.styles?.textTransform || 'none'
                  }}
                >
                  {row.text}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : null;

    case 'media':
    case 'video_with_button_overlay': {
      // Robust media rendering logic shared across preview and published blog
      const looksLikeLink = (s) => typeof s === 'string' && s.length > 5 && s.includes('.') && s !== 'Click Here' && !s.includes(' ');
      const src = looksLikeLink(element.videoFile) ? element.videoFile :
        (looksLikeLink(element.mediaUrl) ? element.mediaUrl :
          (looksLikeLink(element.videoSrc) ? element.videoSrc : null));

      const isVideo = src?.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i);
      const isYouTube = src?.includes('youtube.com') || src?.includes('youtu.be');

      return (
        <Box
          key={element.id || index}
          sx={{
            width: '100%',
            mx: 0,
            pt: index === 0 ? 0 : `${BLOG_ELEMENT_PADDING.top}px`,
            pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
            pl: `${BLOG_ELEMENT_PADDING.mediaLeft}px`,
            pr: `${BLOG_ELEMENT_PADDING.mediaRight}px`,
            transform: `scale(${elementSize / 100})`,
            transformOrigin: 'top center',
            position: 'relative'
          }}
        >
          <Box sx={{
            position: 'relative',
            width: '100%',
            backgroundColor: '#000',
            borderRadius: element.styles?.mediaBorderRadius || (element.styles?.borderRadius && !element.showOverlayButton ? element.styles.borderRadius : '0px'),
            overflow: 'hidden',
            // Padding hack for aspect ratio compatibility
            pt: (() => {
              const ratio = element.styles?.aspectRatio || '16/9';
              const [w, h] = ratio.split('/').map(Number);
              return w && h ? `${(h / w) * 100}%` : '56.25%';
            })(),
            minHeight: '150px'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {!src ? (
                <Box sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  backgroundColor: '#111',
                  color: '#444'
                }}>
                  <PlayArrow sx={{ fontSize: 60, opacity: 0.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    No media uploaded
                  </Typography>
                </Box>
              ) : isVideo ? (
                <CenteredVideoPlayer
                  src={src}
                  poster={element.thumbnailUrl}
                  onPlayTracked={() => {
                    if (blogPostId) {
                      apiService.trackVideoPlay(blogPostId, affiliateId, offerId, sessionId, pageNumber, src, sourceBlogId, offerElementId, pageId)
                        .catch(err => console.error('Failed to track video play:', err));
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: element.styles?.objectFit || 'contain'
                  }}
                />
              ) : isYouTube ? (
                (() => {
                  const getYouTubeID = (url) => {
                    if (!url) return '';
                    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                    const match = url.match(regExp);
                    return (match && match[2].length === 11) ? match[2] : null;
                  };
                  const videoId = getYouTubeID(src);
                  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : src;
                  return (
                    <iframe
                      src={embedUrl}
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      allowFullScreen
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none'
                      }}
                    />
                  );
                })()
              ) : (
                <img
                  src={src}
                  alt="Blog media"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: element.styles?.objectFit || 'contain',
                    display: 'block'
                  }}
                />
              )}

              {/* Overlay Button */}
              {(element.type === 'video_with_button_overlay' || element.showOverlayButton) && (
                (() => {
                  const ratio = element.styles?.aspectRatio || '16/9';
                  const [w, h] = ratio.split('/').map(Number);
                  const isVertical = w && h && h > w;

                  // Calculate dynamic bottom position based on aspect ratio to avoid covering the bottom black bar (60px)
                  // For 9:16 (1.77 ratio), we need roughly 80px to clear the 60px bar if centered in 750px frame
                  // For 9:19 (2.11 ratio), we need even more (roughly 140px)
                  let dynamicBottom = '24px';
                  if (isVertical) {
                    const ratioValue = h / w;
                    if (ratioValue > 2) {
                      dynamicBottom = '140px'; // 9:19 and taller
                    } else if (ratioValue > 1.5) {
                      dynamicBottom = '80px'; // 9:16
                    } else if (ratioValue > 1) {
                      dynamicBottom = '60px'; // Other vertical (portrait, 4:5, etc)
                    }
                  }

                  return (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: dynamicBottom,
                        left: element.styles?.textAlign === 'center' ? '50%' : (element.styles?.textAlign === 'right' ? 'auto' : '24px'),
                        right: element.styles?.textAlign === 'right' ? '24px' : 'auto',
                        transform: element.styles?.textAlign === 'center' ? 'translateX(-50%)' : 'none',
                        width: '55%',
                        zIndex: 100,
                        display: 'flex',
                        justifyContent: element.styles?.textAlign === 'center' ? 'center' : (element.styles?.textAlign === 'right' ? 'flex-end' : 'flex-start')
                      }}
                    >
                      <Button
                        variant="contained"
                        href={processUrl(element.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          background: element.styles?.background || (element.styles?.backgroundColor || '#1976d2'),
                          color: element.styles?.color || '#ffffff',
                          borderRadius: element.styles?.borderRadius || '8px',
                          fontWeight: element.styles?.fontWeight || 'bold',
                          fontSize: element.styles?.fontSize || '16px',
                          padding: element.styles?.padding || '12px 24px',
                          boxShadow: element.styles?.boxShadow || '0 4px 12px rgba(0,0,0,0.3)',
                          textTransform: element.styles?.textTransform || 'none',
                          width: '100%',
                          height: '44px', // Reduced height
                          maxHeight: '44px',
                          '&:hover': {
                            opacity: 0.9,
                            transform: 'scale(1.02)'
                          },
                          transition: 'all 0.2s ease-in-out',
                          ...element.styles // Apply all preset properties
                        }}
                      >
                        {element.content || 'Click Here'}
                      </Button>
                    </Box>
                  );
                })()
              )}
            </Box>
          </Box>
        </Box>
      );
    }

    case 'button':
      return element.content ? (
        <Box
          key={element.id || index}
          sx={{
            textAlign: element.styles?.textAlign || 'center',
            pt: `${BLOG_ELEMENT_PADDING.top}px`,
            pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
            pl: `${BLOG_ELEMENT_PADDING.left}px`,
            pr: `${BLOG_ELEMENT_PADDING.right}px`,
            transform: `scale(${elementSize / 100})`,
            transformOrigin: 'top center',
          }}
        >
          <Button
            variant="contained"
            href={processUrl(element.url)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: element.styles?.backgroundColor || '#1976d2',
              color: element.styles?.color || 'white',
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              fontWeight: element.styles?.fontWeight || 'bold',
              padding: element.styles?.padding || '10px 20px',
              borderRadius: element.styles?.borderRadius || '4px',
              textTransform: element.styles?.textTransform || 'none',
              '&:hover': {
                backgroundColor: element.styles?.hoverBackgroundColor || element.styles?.backgroundColor || '#1565c0'
              },
              ...element.styles
            }}
          >
            {element.content}
          </Button>
        </Box>
      ) : null;

    case 'offer':
      return element.htmlContent ? (
        <OfferElementRenderer
          element={element}
          index={index}
          elementSize={elementSize}
          affiliateId={affiliateId}
          offerId={offerId || element.offerId}
          pageNumber={pageNumber}
          pageId={pageId}
          blogPostId={blogPostId}
          sourceBlogId={sourceBlogId}
          sessionId={sessionId}
        />
      ) : null;

    case 'blackbar':
      return (
        <Box
          key={element.id || index}
          sx={{
            backgroundColor: element.styles?.backgroundColor || '#000000',
            height: element.styles?.height || '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: element.styles?.borderRadius || '0px',
            px: 3,
            pt: `${BLOG_ELEMENT_PADDING.top}px`,
            pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
            transform: `scale(${elementSize / 100})`,
            transformOrigin: 'top center',
            ...element.styles
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {(element.styles?.buttons || []).map((button, buttonIndex) => (
              <Button
                key={buttonIndex}
                variant={button.variant || 'contained'}
                color={button.color || 'primary'}
                size={button.size || 'medium'}
                sx={{
                  backgroundColor: button.backgroundColor || 'inherit',
                  color: button.color || 'white',
                  '&:hover': {
                    backgroundColor: button.hoverColor || 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                {button.text || 'Button'}
              </Button>
            ))}
            {(element.styles?.buttons || []).length === 0 && (
              <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
                Black Bar
              </Typography>
            )}
          </Box>
        </Box>
      );

    default:
      return null;
  }
};

// Separate component for offer elements to handle video fixes and click tracking
const OfferElementRenderer = ({ element, index, elementSize, affiliateId, offerId, pageNumber, pageId, blogPostId, sourceBlogId, sessionId }) => {
  const containerRef = useRef(null);
  const clickHandlersRef = useRef(new Map());
  const playHandlersRef = useRef(new Map());
  const trackedVideosRef = useRef(new WeakSet()); // Track which videos have been recorded to avoid double counting

  useEffect(() => {
    // Fix iOS auto-fullscreen for videos in offer elements and handle click tracking
    const container = containerRef.current;
    if (!container) return;

    const clickHandlers = clickHandlersRef.current;
    const playHandlers = playHandlersRef.current;

    // iOS-specific: Fix poster/thumbnail positioning
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Find all video elements inside the offer element
    const videos = container.querySelectorAll('video');

    videos.forEach((video) => {
      // Add playsInline and webkit-playsinline to prevent iOS auto fullscreen
      video.setAttribute('playsInline', '');
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('playsinline', 'true');

      // Also ensure controls are present if not already set
      if (!video.hasAttribute('controls')) {
        video.setAttribute('controls', '');
      }

      // iOS-specific: Measure and fix gap dynamically
      if (isIOS) {
        // Find the outermost wrapper div (with text-align: center)
        let outerWrapper = null;
        let current = video.parentElement;
        while (current && current !== container) {
          const style = window.getComputedStyle(current);
          if (style.textAlign === 'center' || current.style.textAlign === 'center') {
            outerWrapper = current;
            break;
          }
          current = current.parentElement;
        }

        // Function to measure and fix gap
        const measureAndFixGap = () => {
          if (!outerWrapper) return;

          // Get container top position
          const containerRect = container.getBoundingClientRect();
          const wrapperRect = outerWrapper.getBoundingClientRect();
          const videoRect = video.getBoundingClientRect();

          // Calculate gap from container top to wrapper top
          const gap = wrapperRect.top - containerRect.top;

          if (gap > 0) {
            // Apply negative margin to counteract the gap
            outerWrapper.style.setProperty('margin-top', `-${gap}px`, 'important');
            outerWrapper.style.setProperty('padding-top', '0', 'important');
            outerWrapper.style.setProperty('line-height', '0', 'important');
            outerWrapper.style.setProperty('font-size', '0', 'important');

            // Also fix aspect ratio container
            let aspectContainer = video.parentElement;
            while (aspectContainer && aspectContainer !== container) {
              const style = window.getComputedStyle(aspectContainer);
              if (style.position === 'relative' && style.paddingBottom && parseFloat(style.paddingBottom) > 0) {
                aspectContainer.style.setProperty('margin-top', '0', 'important');
                aspectContainer.style.setProperty('padding-top', '0', 'important');
                aspectContainer.style.setProperty('top', '0', 'important');
                break;
              }
              aspectContainer = aspectContainer.parentElement;
            }

            // Fix video element
            video.style.setProperty('margin-top', '0', 'important');
            video.style.setProperty('padding-top', '0', 'important');
            video.style.setProperty('vertical-align', 'top', 'important');
            video.style.setProperty('display', 'block', 'important');
          }
        };

        // Measure gap immediately
        setTimeout(measureAndFixGap, 0);
        requestAnimationFrame(() => {
          setTimeout(measureAndFixGap, 0);
        });

        // Force video to load metadata
        if (video.hasAttribute('poster')) {
          video.load();
        }

        // Measure and fix when metadata loads
        const fixOnMetadataLoad = () => {
          setTimeout(() => {
            measureAndFixGap();
            // Force reflow
            void video.offsetHeight;
            void outerWrapper?.offsetHeight;
          }, 0);
        };

        video.addEventListener('loadedmetadata', fixOnMetadataLoad, { once: true });
        video.addEventListener('loadeddata', fixOnMetadataLoad, { once: true });
        video.addEventListener('canplay', fixOnMetadataLoad, { once: true });

        // Also try to trigger layout recalculation by simulating play
        video.addEventListener('loadedmetadata', () => {
          // Flag as synthetic play to prevent tracking it as a real user interaction
          video.__isSyntheticPlay = true;

          // Try a brief play/pause to trigger layout recalculation
          const playPromise = video.play().catch(() => { });
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.then(() => {
              video.pause();
              video.currentTime = 0;
              video.__isSyntheticPlay = false;
              setTimeout(measureAndFixGap, 50);
            }).catch(() => {
              video.__isSyntheticPlay = false;
              setTimeout(measureAndFixGap, 100);
            });
          } else {
            video.__isSyntheticPlay = false;
          }
        }, { once: true });
      }

      const handleVideoPlay = (e) => {
        const targetVideo = e.target;

        // Skip tracking if this is our own synthetic play for iOS gap fix
        if (targetVideo && targetVideo.__isSyntheticPlay) {
          return;
        }

        // Only track each video play once per page load to avoid duplicate counts on play/pause
        if (targetVideo && trackedVideosRef.current.has(targetVideo)) {
          return;
        }

        if (blogPostId) {
          if (targetVideo) {
            trackedVideosRef.current.add(targetVideo);
          }

          const currentOfferElementId = element.id || element._id || null;
          apiService.trackVideoPlay(blogPostId, affiliateId, offerId, sessionId, pageNumber, targetVideo ? targetVideo.src : 'offer-video', sourceBlogId, currentOfferElementId, pageId)
            .catch(err => console.error('Failed to track video play:', err));
        }
      };

      // Clean up previous listeners if any to avoid duplicates
      if (playHandlers.has(video)) {
        video.removeEventListener('play', playHandlers.get(video));
      }
      playHandlers.set(video, handleVideoPlay);
      video.addEventListener('play', handleVideoPlay);
    });

    // Handle click tracking for buttons and links
    const handleButtonClick = (e) => {
      // ALWAYS prevent default first to stop any direct navigation
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      console.log('🖱️ Click handler triggered:', {
        elementType: element.type,
        elementId: element.id,
        target: e.target,
        currentTarget: e.currentTarget,
        hasAffiliateId: !!affiliateId,
        hasOfferId: !!offerId,
        affiliateId,
        offerId,
        href: e.target.href || e.target.getAttribute('href') || e.currentTarget?.href || e.currentTarget?.getAttribute('href')
      });

      // Only handle if we have affiliate ID and offer ID
      if (!affiliateId || !offerId) {
        console.error('❌ Click Tracking: Missing affiliateId or offerId for click tracking', {
          affiliateId,
          offerId,
          elementId: element.id,
          elementType: element.type
        });
        // Still prevent default even if we can't track
        return false;
      }

      const target = e.target.closest('a, button') || e.currentTarget;
      if (!target) {
        console.error('❌ Click Tracking: Could not find target element');
        return false;
      }

      // Check if it's already a redirect API link
      const href = target.href || target.getAttribute('href');
      console.log('🔍 Click Tracking: Checking href:', href);

      if (href && href.includes('/api/affiliate/redirect/')) {
        // Check if session_id is already in the URL
        if (href.includes('session_id=')) {
          console.log('✅ Click Tracking: Already a redirect link with session_id, opening in new tab', { href });
          // Open in new tab, keeping the blog page open
          const newWindow = window.open(href, '_blank', 'noopener,noreferrer');
          if (!newWindow) {
            console.warn('⚠️ Pop-up blocked. Please allow popups for this site to open offer links.');
          }
          return false;
        }

        // Redirect link exists but missing session_id - add it
        console.log('🔧 Click Tracking: Redirect link exists but missing session_id, adding it', { href });
        e.preventDefault();
        e.stopPropagation();

        // Get session ID for linking page views to clicks
        const sessionId = sessionTracking.getSessionId();

        // Add session_id and page_number to the URL
        try {
          // Handle both absolute and relative URLs
          const url = href.startsWith('http') ? new URL(href) : new URL(href, window.location.origin);
          url.searchParams.set('session_id', sessionId);
          // Add page number if available
          if (pageNumber !== undefined && pageNumber !== null) {
            url.searchParams.set('page_number', pageNumber.toString());
          }
          if (pageId) {
            url.searchParams.set('page_id', pageId.toString());
          }
          const updatedUrl = url.toString();

          console.log('🔧 Click Tracking: Updated redirect URL with session_id and page_number:', updatedUrl);
          console.log('  📍 Original URL:', href);
          console.log('  🆔 Session ID added:', sessionId);
          console.log('  📄 Page Number added:', pageNumber);
          const newWindow = window.open(updatedUrl, '_blank', 'noopener,noreferrer');
          if (!newWindow) {
            console.warn('⚠️ Pop-up blocked. Please allow popups for this site to open offer links.');
          }
        } catch (error) {
          console.error('❌ Error updating redirect URL with session_id:', error);
          // Fallback: manually append session_id and page_number
          const separator = href.includes('?') ? '&' : '?';
          const pageNumberParam = (pageNumber !== undefined && pageNumber !== null) ? `&page_number=${pageNumber}` : '';
          const pageIdParam = pageId ? `&page_id=${pageId}` : '';
          const updatedUrl = `${href}${separator}session_id=${sessionId}${pageNumberParam}${pageIdParam}`;
          console.log('🔧 Click Tracking: Using fallback URL update:', updatedUrl);
          const newWindow2 = window.open(updatedUrl, '_blank', 'noopener,noreferrer');
          if (!newWindow2) {
            console.warn('⚠️ Pop-up blocked. Please allow popups for this site to open offer links.');
          }
        }
        return;
      }

      // Already prevented default above, but ensure it stays prevented

      // Generate redirect API URL (API will generate click ID)
      // Use actual hostname for mobile/LAN access instead of localhost
      let serverUrl = config.SERVER_URL || 'http://localhost:8080';

      // Always use current hostname when available (for mobile/LAN access)
      if (typeof window !== 'undefined' && window.location) {
        const currentHostname = window.location.hostname;
        const currentProtocol = window.location.protocol;

        // If we're accessing from a non-localhost address (e.g., mobile on LAN at 192.168.x.x)
        // or if config has localhost but we're not on localhost, use current hostname
        if (currentHostname !== 'localhost' && currentHostname !== '127.0.0.1') {
          // For production/deployed builds, don't include port number (standard ports 80/443)
          if (config.IS_PRODUCTION) {
            // Production: use protocol + hostname without port
            serverUrl = `${currentProtocol}//${currentHostname}`;
            console.log('🌐 Using production server URL (no port):', serverUrl);
          } else {
            // Development/LAN: extract port from config URL (default to 8080 for backend)
            const urlMatch = serverUrl.match(/:(\d+)/);
            const backendPort = urlMatch ? urlMatch[1] : '8080';
            // Use current hostname with backend port
            serverUrl = `${currentProtocol}//${currentHostname}:${backendPort}`;
            console.log('🌐 Using detected server URL for mobile/LAN access:', serverUrl);
            console.log('  📍 Current hostname:', currentHostname);
            console.log('  🔌 Backend port:', backendPort);
          }
        }
      }

      // Get session ID for linking page views to clicks
      // Use the same sessionTracking utility to ensure same session ID as page views
      const sessionId = sessionTracking.getSessionId();

      // Normalize IDs to strings (handle both string and object formats)
      const normalizedOfferId = offerId?._id ? offerId._id.toString() : (offerId?.toString() || offerId);
      const normalizedAffiliateId = affiliateId?._id ? affiliateId._id.toString() : (affiliateId?.toString() || affiliateId);

      // Build redirect URL with full context for direct click logging
      const redirectUrlParams = new URLSearchParams({
        aff_id: normalizedAffiliateId,
        session_id: sessionId
      });

      // Add page number (0-indexed, matching pageNumber in BlogPageView)
      if (pageNumber !== undefined && pageNumber !== null) {
        redirectUrlParams.set('page_number', pageNumber.toString());
      }
      // Add page ID if available
      if (pageId) {
        redirectUrlParams.set('page_id', pageId.toString());
      }

      // Add blog post ID (head blog) and source snake blog ID for stable attribution
      if (blogPostId) {
        redirectUrlParams.set('blog_post_id', blogPostId.toString());
      }
      // source_blog_id is the actual per-affiliate snake source blog — stable even if snake reorders
      if (sourceBlogId) {
        redirectUrlParams.set('source_blog_id', sourceBlogId.toString());
      }

      // Add offer element context for rich reporting
      if (element.offerName) {
        redirectUrlParams.set('offer_name', element.offerName);
      }
      if (element.elementName) {
        redirectUrlParams.set('offer_element_name', element.elementName);
      }
      if (element.id) {
        redirectUrlParams.set('offer_element_id', element.id);
      }

      const redirectUrl = `${serverUrl}/api/affiliate/redirect/${normalizedOfferId}?${redirectUrlParams.toString()}`;


      // Detailed logging for debugging
      console.log('🔍 Click Tracking Debug Info:');
      console.log('  📍 Timestamp:', new Date().toISOString());
      console.log('  👤 Affiliate ID (raw):', affiliateId);
      console.log('  👤 Affiliate ID (normalized):', normalizedAffiliateId);
      console.log('  🎯 Offer ID (raw):', offerId);
      console.log('  🎯 Offer ID (normalized):', normalizedOfferId);
      console.log('  📄 Page Number:', pageNumber);
      console.log('  📄 Element ID:', element.id);
      console.log('  📝 Element Name:', element.elementName);
      console.log('  🏷️  Offer Name:', element.offerName);
      console.log('  🆔 Session ID:', sessionId);
      console.log('  🌐 Server URL:', serverUrl);
      console.log('  🔗 Redirect URL:', redirectUrl);
      console.log('  ⚠️  Note: Click ID will be generated by the API');
      console.log('  → Redirecting to API...');

      // Navigate to redirect API in a new tab, keeping blog page open
      try {
        const newWindow = window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Popup blocked - inform user but keep blog page open
          console.warn('⚠️ Pop-up blocked. Please allow popups for this site to open offer links.');
          console.log('  🔗 Blocked URL:', redirectUrl);
        }
      } catch (error) {
        console.error('❌ Error opening redirect URL in new tab:', error);
        console.warn('⚠️ Could not open link. Please allow popups and try again.');
      }
    };

    // Find all buttons and links
    const buttons = container.querySelectorAll('a, button');
    console.log(`🔍 [Offer Click Tracking] Found ${buttons.length} buttons/links in offer element`, {
      elementId: element.id,
      hasAffiliateId: !!affiliateId,
      hasOfferId: !!offerId,
      affiliateId,
      offerId
    });

    buttons.forEach((button, index) => {
      const href = button.href || button.getAttribute('href');
      const isRedirectLink = href && href.includes('/api/affiliate/redirect/');
      const hasSessionId = href && href.includes('session_id=');

      console.log(`🔍 [Offer Click Tracking] Button ${index + 1}:`, {
        tagName: button.tagName,
        href: href || 'no href',
        isRedirectLink,
        hasSessionId,
        willAttachHandler: affiliateId && offerId && (!isRedirectLink || !hasSessionId)
      });

      // ALWAYS attach handler if we have affiliateId and offerId, regardless of href
      // This ensures we intercept ALL clicks and route them through our tracking endpoint
      if (affiliateId && offerId) {
        // Remove any existing click handlers first to avoid duplicates
        button.removeEventListener('click', handleButtonClick);

        // Also remove href temporarily to force handler to fire, then restore it
        const originalHref = button.href || button.getAttribute('href');
        if (originalHref && originalHref.includes('/api/affiliate/redirect/') && !originalHref.includes('session_id=')) {
          // Store original href but remove it so our handler can intercept
          button.setAttribute('data-original-href', originalHref);
          button.removeAttribute('href');
          if (button.tagName === 'A') {
            button.style.cursor = 'pointer';
          }
        }

        // Attach our handler with capture phase to intercept before default behavior
        button.addEventListener('click', handleButtonClick, true);
        clickHandlers.set(button, handleButtonClick);

        if (isRedirectLink && !hasSessionId) {
          console.log(`✅ [Offer Click Tracking] Attached click handler to button ${index + 1} to add session_id`, {
            originalHref,
            willIntercept: true
          });
        } else if (!isRedirectLink) {
          console.log(`✅ [Offer Click Tracking] Attached click handler to button ${index + 1} to intercept direct link and route through tracking`);
        } else {
          console.log(`✅ [Offer Click Tracking] Attached click handler to button ${index + 1} (already has redirect with session_id, but keeping handler for safety)`);
        }
      } else {
        console.warn(`⚠️  [Offer Click Tracking] Cannot attach handler to button ${index + 1}:`, {
          hasAffiliateId: !!affiliateId,
          hasOfferId: !!offerId,
          affiliateId,
          offerId
        });
      }
    });

    // Also handle dynamically added videos and buttons
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Handle videos
            if (node.tagName === 'VIDEO') {
              node.setAttribute('playsInline', '');
              node.setAttribute('webkit-playsinline', 'true');
              node.setAttribute('playsinline', 'true');

              // iOS-specific: Measure and fix gap
              if (isIOS) {
                setTimeout(() => {
                  let outerWrapper = null;
                  let current = node.parentElement;
                  while (current && current !== container) {
                    const style = window.getComputedStyle(current);
                    if (style.textAlign === 'center' || current.style.textAlign === 'center') {
                      outerWrapper = current;
                      break;
                    }
                    current = current.parentElement;
                  }

                  if (outerWrapper) {
                    const containerRect = container.getBoundingClientRect();
                    const wrapperRect = outerWrapper.getBoundingClientRect();
                    const gap = wrapperRect.top - containerRect.top;
                    if (gap > 0) {
                      outerWrapper.style.setProperty('margin-top', `-${gap}px`, 'important');
                      outerWrapper.style.setProperty('padding-top', '0', 'important');
                    }
                  }
                }, 0);
              }
            }
            // Check for videos in added nodes
            const videos = node.querySelectorAll?.('video');
            videos?.forEach((video) => {
              video.setAttribute('playsInline', '');
              video.setAttribute('webkit-playsinline', 'true');
              video.setAttribute('playsinline', 'true');

              // iOS-specific: Measure and fix gap
              if (isIOS) {
                setTimeout(() => {
                  let outerWrapper = null;
                  let current = video.parentElement;
                  while (current && current !== container) {
                    const style = window.getComputedStyle(current);
                    if (style.textAlign === 'center' || current.style.textAlign === 'center') {
                      outerWrapper = current;
                      break;
                    }
                    current = current.parentElement;
                  }

                  if (outerWrapper) {
                    const containerRect = container.getBoundingClientRect();
                    const wrapperRect = outerWrapper.getBoundingClientRect();
                    const gap = wrapperRect.top - containerRect.top;
                    if (gap > 0) {
                      outerWrapper.style.setProperty('margin-top', `-${gap}px`, 'important');
                      outerWrapper.style.setProperty('padding-top', '0', 'important');
                    }
                  }
                }, 0);
              }
            });

            // Handle generic dynamically added videos too
            videos?.forEach((video) => {
              if (blogPostId) {
                const handleDynamicVideoPlay = (e) => {
                  const targetVideo = e.target;

                  // Skip tracking if synthetic
                  if (targetVideo && targetVideo.__isSyntheticPlay) return;

                  // Track only once
                  if (targetVideo && trackedVideosRef.current.has(targetVideo)) return;

                  if (targetVideo) {
                    trackedVideosRef.current.add(targetVideo);
                  }

                  const currentOfferElementId = element.id || element._id || null;
                  apiService.trackVideoPlay(blogPostId, affiliateId, offerId, sessionId, pageNumber, targetVideo ? targetVideo.src : 'offer-video', sourceBlogId, currentOfferElementId, pageId)
                    .catch(err => console.error('Failed to dynamic track video play:', err));
                };
                if (playHandlers.has(video)) {
                  video.removeEventListener('play', playHandlers.get(video));
                }
                playHandlers.set(video, handleDynamicVideoPlay);
                video.addEventListener('play', handleDynamicVideoPlay);
              }
            });

            // Handle buttons and links
            if (node.tagName === 'A' || node.tagName === 'BUTTON') {
              const href = node.href || node.getAttribute('href');
              const isRedirectLink = href && href.includes('/api/affiliate/redirect/');
              const hasSessionId = href && href.includes('session_id=');
              if (affiliateId && offerId && (!isRedirectLink || !hasSessionId)) {
                console.log('🔍 [Offer Click Tracking] Attaching handler to dynamically added button/link:', {
                  tagName: node.tagName,
                  href: href || 'no href',
                  isRedirectLink,
                  hasSessionId
                });
                node.addEventListener('click', handleButtonClick);
                clickHandlers.set(node, handleButtonClick);
              }
            }
            // Check for buttons/links in added nodes
            const buttons = node.querySelectorAll?.('a, button');
            buttons?.forEach((button) => {
              const href = button.href || button.getAttribute('href');
              const isRedirectLink = href && href.includes('/api/affiliate/redirect/');
              const hasSessionId = href && href.includes('session_id=');
              if (affiliateId && offerId && (!isRedirectLink || !hasSessionId)) {
                console.log('🔍 [Offer Click Tracking] Attaching handler to dynamically added nested button/link:', {
                  tagName: button.tagName,
                  href: href || 'no href',
                  isRedirectLink,
                  hasSessionId
                });
                button.addEventListener('click', handleButtonClick);
                clickHandlers.set(button, handleButtonClick);
              }
            });
          }
        });
      });
    });

    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      // Clean up event listeners
      clickHandlers.forEach((handler, button) => {
        button.removeEventListener('click', handler);
      });
      clickHandlers.clear();

      playHandlers.forEach((handler, video) => {
        video.removeEventListener('play', handler);
      });
      playHandlers.clear();
    };
  }, [element.htmlContent, affiliateId, offerId]);

  return (
    <Box
      key={element.id || index}
      sx={{
        width: '100%',
        height: 'auto',
        // Full width - extend beyond container padding
        mx: 0,
        mt: index === 0 ? 0 : 0, // No top margin - border should align with container top
        mb: 0, // No bottom margin
        pt: 0, // No top padding
        pb: `${BLOG_ELEMENT_PADDING.bottom}px`,
        pl: `${BLOG_ELEMENT_PADDING.mediaLeft}px`,
        pr: `${BLOG_ELEMENT_PADDING.mediaRight}px`,
        border: process.env.NODE_ENV === 'development' ? '4px solid #2196F3 !important' : 'none',
        borderRadius: 2,
        backgroundColor: '#f8f9fa',
        minHeight: '50px',
        transform: `scale(${elementSize / 100})`,
        transformOrigin: 'top center', // Align transform origin to top
        position: 'relative',
        zIndex: 1,
        overflow: 'visible', // Allow videos to extend beyond padding
        // When first element, ensure no top spacing
        ...(index === 0 && {
          marginTop: '0 !important',
          paddingTop: '0 !important'
        })
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          pt: 0, // No top padding
          pb: 1, // Reduced bottom padding
          px: 0, // No horizontal padding - videos take full width
          '& > *:first-child': {
            mt: 0, // Remove top margin from first child
            pt: 0 // Remove top padding from first child
          },
          // iOS-specific: Fix wrapper div structure to prevent gap
          '@supports (-webkit-touch-callout: none)': {
            // Target wrapper divs with text-align: center that contain videos
            '& > div[style*="text-align: center"]:first-child': {
              display: 'flex !important',
              flexDirection: 'column !important',
              alignItems: 'stretch !important',
              marginTop: '0 !important',
              paddingTop: '0 !important',
              lineHeight: '0 !important',
              fontSize: '0 !important'
            },
            '& video': {
              marginTop: '0 !important',
              paddingTop: '0 !important',
              verticalAlign: 'top !important',
              display: 'block !important'
            },
            // Target aspect ratio containers
            '& div[style*="position: relative"]': {
              marginTop: '0 !important',
              paddingTop: '0 !important',
              top: '0 !important',
              lineHeight: '0 !important'
            }
          },
          // Preserve original HTML layout and styling - no CSS overrides
          '& *': {
            // Only ensure images don't overflow container, but preserve their original styling
            maxWidth: '100%'
          },
          // Ensure videos take full width and extend beyond container padding
          '& video, & iframe[src*="youtube"], & iframe[src*="vimeo"], & iframe': {
            width: '100% !important', // Full width
            maxWidth: '100% !important',
            height: 'auto !important',
            display: 'block !important',
            margin: '0 !important',
            padding: '0 !important'
          },
          // Ensure video wrapper divs take full width and extend beyond padding (only for videos)
          '& div:has(> video) div[style*="position: relative"]': {
            width: 'calc(100% + 32px) !important',
            maxWidth: 'calc(100% + 32px) !important',
            margin: '0 -16px !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          },
          // Ensure parent wrapper divs also take full width (only for videos)
          '& div:has(> video) div[style*="text-align: center"]': {
            width: '100% !important',
            maxWidth: '100% !important',
            margin: '0 !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          },
          // Target divs that contain videos specifically - apply to all ancestor divs
          // BUT preserve padding-bottom for aspect ratio containers
          '& div:has(> video)': {
            width: '100% !important',
            maxWidth: '100% !important',
            margin: '0 !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
            // Preserve padding-bottom if it exists (for aspect ratio containers)
            // Don't override paddingBottom - let the inline style handle it
          },
          '& div:has(video)': {
            width: '100% !important',
            maxWidth: '100% !important',
            margin: '0 !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
            // Preserve padding-bottom if it exists (for aspect ratio containers)
            // Don't override paddingBottom - let the inline style handle it
          },
          // Preserve margins and padding for buttons and images to prevent overlap
          '& > div > a, & > div > button, & a[href], & button': {
            marginTop: '8px !important',
            marginBottom: '8px !important',
            display: 'block !important'
          },
          '& > div > img, & img': {
            marginBottom: '8px !important',
            marginTop: '8px !important',
            display: 'block'
          },
          // Ensure spacing between wrapper divs that contain buttons/images
          '& > div': {
            marginBottom: '8px',
            '&:last-child': {
              marginBottom: 0
            }
          }
        }}
        dangerouslySetInnerHTML={{ __html: element.htmlContent }}
      />
    </Box>
  );
};

export default BlogElementRenderer;

